import React, { useState, Suspense } from "react"
import PropTypes from "prop-types"
import Stack from "@kit/design/stack"
import { useBlocks } from "../../hooks/use-blocks"
import { ImageActions } from "core/image-actions"
import { useToggle } from "hooks/use-toggle"
import { isFileKitURL } from "utils/is-filekit-url"
import CheckboxField from "@kit/design/checkbox-field"
import TextField from "@kit/design/text-field"
import FileKit from "@convertkit/file-kit"
import { useCurrentUser } from "hooks/use-current-user"
import { handleDelete } from "../setting-utils"

import { FileGallery } from "core/file-gallery"

const DokaModal = React.lazy(() => import("vendor/lazy-doka.js"))
const CircularMask = `<mask id="my-mask">
     <rect x="0" y="0" width="100%" height="100%" fill="white"/>
     <circle cx="50%" cy="50%" r="50%" fill="black"/>
     </mask>
     <rect fill="rgba(255,255,255,.3125)" x="0" y="0" width="100%" height="100%" mask="url(#my-mask)"/>
     <circle cx="50%" cy="50%" r="50%" fill="transparent" stroke-width="1" stroke="#fff"/>`
const maskShapes = {
  circle: CircularMask
}
export function ImageSettings({ block_id, properties }) {
  const { update, remove } = useBlocks(block_id)

  const removeImage = () => {
    handleDelete(block_id, remove, "image")
  }

  const [active, toggle] = useToggle(false)
  const [showDokaModal, setShowDokaModal] = useState(false)
  const src = properties.src
  const user = useCurrentUser()

  const aspectRatio = properties ? properties.aspectRatio : undefined
  const mask = properties ? maskShapes[properties.shape] : undefined
  const optimize =
    properties.optimize === undefined ? true : properties.optimize

  const closeFileGallery = upload => {
    toggle()

    if (!upload?.dataURL) {
      return
    }

    update({
      src: upload.dataURL,
      alt: upload.alt
    })
  }

  const handleChange = event => {
    const { name, value } = event.target

    update({ [name]: value })
  }

  const handleEditCancel = () => {
    setShowDokaModal(false)
  }

  const handleEditConfirm = async output => {
    setShowDokaModal(false)

    const filekit = FileKit({
      authURL: "/filekit/sessions",
      filekitURL: process.env.FILEKIT_HOST,
      id: user.account_id
    })
    const parentPath = FileKit.pathFromURL(src)
    const result = await filekit.upload(output.file, parentPath)

    update({
      src: result.original_url
    })
  }

  return (
    <Stack spacing={2} className="px-4 pb-4">
      <div className="flex">
        <ImageActions
          imageEditorButtonVisible={!!src}
          showDokaModal={setShowDokaModal}
          openFileGallery={toggle}
          removeImage={removeImage}
          source={src}
        />
      </div>
      <FileGallery
        active={active}
        onClose={closeFileGallery}
        filterOnFileType={"image"}
      />
      <Suspense fallback={null}>
        {showDokaModal && (
          <DokaModal
            style={{ maxWidth: "200px", height: "200px" }}
            src={src}
            onConfirm={handleEditConfirm}
            onCancel={handleEditCancel}
            cropAspectRatio={aspectRatio}
            cropMask={(root, setInnerHTML) => setInnerHTML(root, mask)}
          />
        )}
      </Suspense>
      {isFileKitURL(src) && (
        <>
          <CheckboxField
            label="Optimize image"
            onChange={event => {
              update({ optimize: event.target.checked })
            }}
            name="optimize"
            value={optimize}
          />
          <p className="subtext text-xs pt-1">
            Improves loading time for your email.
          </p>
        </>
      )}
      <TextField
        label="URL"
        name="url"
        onChange={handleChange}
        value={properties.url || ""}
        placeholder="www.kit.com"
      />
      <TextField
        label="Alt Text"
        name="alt"
        onChange={handleChange}
        value={properties.alt || ""}
        placeholder="a person typing an email"
      />
    </Stack>
  )
}

ImageSettings.propTypes = {
  properties: PropTypes.object,
  block_id: PropTypes.string
}
